import { useState, type FormEvent } from 'react';
import { Car, Pencil, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { EmptyState } from '@/components/empty-state';
import { Input } from '@/components/input';
import { Menu } from '@/components/menu';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { dollars, formatDate } from '@/lib/format';
import { useClients } from '@/lib/clients';
import {
  useDeleteTrip,
  useDeleteVehicle,
  useMileageSummary,
  useMileageTrips,
  useSaveTrip,
  useSaveVehicle,
  useSetMileageRate,
  useVehicles,
  type TripInput,
  type VehicleInput,
} from '@/lib/mileage';
import type { MileageTripData, VehicleData } from '@/types/api';

export function MileageTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const summary = useMileageSummary(year);
  const vehicles = useVehicles();
  const trips = useMileageTrips({ year });

  const [vehicleDialog, setVehicleDialog] = useState<{ open: boolean; editing?: VehicleData }>({
    open: false,
  });
  const [tripDialog, setTripDialog] = useState<{ open: boolean; editing?: MileageTripData }>({
    open: false,
  });

  const years = summary.data?.available_years ?? [year];

  return (
    <div className="space-y-5">
      <SummaryCard
        year={year}
        years={years}
        onYear={setYear}
        summary={summary.data}
      />

      <Card>
        <CardHeader className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">Vehicles</h3>
            <p className="text-xs text-[var(--color-foreground-muted)]">
              Vehicles you use for business (fills Schedule C Part IV).
            </p>
          </div>
          <Button variant="secondary" onClick={() => setVehicleDialog({ open: true })}>
            <Plus className="size-4" /> Add vehicle
          </Button>
        </CardHeader>
        <CardBody>
          {vehicles.isLoading ? (
            <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
          ) : !vehicles.data || vehicles.data.length === 0 ? (
            <EmptyState
              icon={Car}
              title="No vehicles yet"
              description="Add the vehicle you drive for business so you can log trips against it."
            />
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {vehicles.data.map((v) => (
                <VehicleRow
                  key={v.id}
                  vehicle={v}
                  onEdit={() => setVehicleDialog({ open: true, editing: v })}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">Trips · {year}</h3>
            <p className="text-xs text-[var(--color-foreground-muted)]">
              Log business trips. Deduction is total miles × the IRS rate.
            </p>
          </div>
          <Button
            onClick={() => setTripDialog({ open: true })}
            disabled={!vehicles.data || vehicles.data.length === 0}
          >
            <Plus className="size-4" /> Log trip
          </Button>
        </CardHeader>
        <CardBody>
          {trips.isLoading ? (
            <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
          ) : !trips.data || trips.data.length === 0 ? (
            <EmptyState
              icon={Car}
              title="No trips logged"
              description={
                vehicles.data && vehicles.data.length > 0
                  ? 'Log your first business trip to start accruing the deduction.'
                  : 'Add a vehicle first, then log trips against it.'
              }
            />
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {trips.data.map((t) => (
                <TripRow key={t.id} trip={t} onEdit={() => setTripDialog({ open: true, editing: t })} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <VehicleDialog
        state={vehicleDialog}
        onClose={() => setVehicleDialog({ open: false })}
      />
      <TripDialog
        state={tripDialog}
        vehicles={vehicles.data ?? []}
        year={year}
        onClose={() => setTripDialog({ open: false })}
      />
    </div>
  );
}

function SummaryCard({
  year,
  years,
  onYear,
  summary,
}: {
  year: number;
  years: number[];
  onYear: (y: number) => void;
  summary: import('@/lib/mileage').MileageSummary | undefined;
}) {
  const setRate = useSetMileageRate();
  const toast = useToast();
  const [rateInput, setRateInput] = useState('');

  async function saveRate() {
    const cents = parseFloat(rateInput);
    if (!(cents > 0)) {
      toast.error('Enter the cents-per-mile rate (e.g. 70)');
      return;
    }
    await setRate.mutateAsync({ year, cents_per_mile: cents });
    toast.success(`${year} rate set`);
    setRateInput('');
  }

  const miles = summary?.total_business_miles ?? 0;

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Standard mileage deduction</h3>
          <p className="text-xs text-[var(--color-foreground-muted)]">
            Tax-only figure for Schedule C line 9 — it does not post to your ledger.
          </p>
        </div>
        <Select value={year} onChange={(e) => onYear(Number(e.target.value))} className="w-auto">
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
      </CardHeader>
      <CardBody>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Stat label="Business miles" value={miles.toFixed(1)} />
          <Stat
            label="IRS rate"
            value={
              summary?.has_rate ? `$${((summary.rate_cents_per_mile ?? 0) / 100).toFixed(2)}/mi` : '—'
            }
          />
          <Stat
            label="Deduction"
            value={summary?.deduction_cents != null ? dollars(summary.deduction_cents) : '—'}
            emphasis
          />
        </div>

        {summary && !summary.has_rate ? (
          <div className="mt-4 flex flex-wrap items-end gap-2 rounded-md bg-[var(--color-surface-muted)] p-3">
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-[var(--color-foreground-muted)]">
                Set the {year} IRS rate (cents per mile)
              </span>
              <Input
                type="number"
                step="0.1"
                placeholder="e.g. 70"
                value={rateInput}
                onChange={(e) => setRateInput(e.target.value)}
                className="w-40"
              />
            </label>
            <Button onClick={saveRate} disabled={setRate.isPending}>
              Save rate
            </Button>
            <span className="text-xs text-[var(--color-foreground-subtle)]">
              We can't compute the deduction until this year's IRS rate is set.
            </span>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function Stat({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-[var(--color-foreground-subtle)]">
        {label}
      </div>
      <div
        className={`mt-0.5 tabular-nums ${emphasis ? 'text-2xl font-semibold' : 'text-lg font-medium'}`}
      >
        {value}
      </div>
    </div>
  );
}

function VehicleRow({ vehicle, onEdit }: { vehicle: VehicleData; onEdit: () => void }) {
  const del = useDeleteVehicle();
  const confirm = useConfirm();
  const toast = useToast();
  const sub = [vehicle.make, vehicle.model, vehicle.year].filter(Boolean).join(' ');

  return (
    <li className="flex items-center gap-3 py-3 text-sm">
      <Car className="size-5 text-[var(--color-foreground-muted)]" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{vehicle.name}</span>
          {!vehicle.is_active ? <Badge tone="neutral">Inactive</Badge> : null}
        </div>
        <div className="text-xs text-[var(--color-foreground-subtle)]">
          {sub || '—'}
          {vehicle.date_placed_in_service
            ? ` · in service ${formatDate(vehicle.date_placed_in_service)}`
            : ''}
        </div>
      </div>
      <Menu
        items={[
          { key: 'edit', label: 'Edit', icon: Pencil, onSelect: onEdit },
          {
            key: 'delete',
            label: 'Delete',
            icon: Trash2,
            danger: true,
            onSelect: async () => {
              const ok = await confirm({
                title: `Delete ${vehicle.name}?`,
                body: 'Only works while nothing has been logged against it. Once a vehicle has trips it stays on file for the mileage deduction, and marking it inactive is what takes it out of the picker.',
                confirmLabel: 'Delete',
                tone: 'danger',
              });
              if (ok) {
                await del.mutateAsync(vehicle.id);
                toast.success('Vehicle deleted');
              }
            },
          },
        ]}
      />
    </li>
  );
}

function TripRow({ trip, onEdit }: { trip: MileageTripData; onEdit: () => void }) {
  const del = useDeleteTrip();
  const confirm = useConfirm();
  const toast = useToast();

  return (
    <li className="flex items-center gap-3 py-3 text-sm">
      <div className="min-w-0 flex-1">
        <div className="font-medium">{trip.purpose}</div>
        <div className="text-xs text-[var(--color-foreground-subtle)]">
          {formatDate(trip.trip_date)}
          {trip.vehicle_name ? ` · ${trip.vehicle_name}` : ''}
          {trip.client_name ? ` · ${trip.client_name}` : ''}
        </div>
      </div>
      <div className="text-right tabular-nums">
        <span className="font-medium">{Number(trip.business_miles).toFixed(1)}</span>
        <span className="text-xs text-[var(--color-foreground-muted)]"> mi</span>
      </div>
      <Menu
        items={[
          { key: 'edit', label: 'Edit', icon: Pencil, onSelect: onEdit },
          {
            key: 'delete',
            label: 'Delete',
            icon: Trash2,
            danger: true,
            onSelect: async () => {
              const ok = await confirm({
                title: 'Delete this trip?',
                body: 'The miles are removed from your deduction.',
                confirmLabel: 'Delete',
                tone: 'danger',
              });
              if (ok) {
                await del.mutateAsync(trip.id);
                toast.success('Trip deleted');
              }
            },
          },
        ]}
      />
    </li>
  );
}

function VehicleDialog({
  state,
  onClose,
}: {
  state: { open: boolean; editing?: VehicleData };
  onClose: () => void;
}) {
  const save = useSaveVehicle();
  const toast = useToast();
  const editing = state.editing;
  const [seeded, setSeeded] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<VehicleInput>({ name: '', available_for_personal_use: true, is_active: true });

  const seedKey = editing ? editing.id : state.open ? 'new' : null;
  if (seedKey !== null && seeded !== seedKey) {
    setForm(
      editing
        ? {
            name: editing.name,
            make: editing.make,
            model: editing.model,
            year: editing.year,
            date_placed_in_service: editing.date_placed_in_service,
            available_for_personal_use: editing.available_for_personal_use,
            has_another_vehicle: editing.has_another_vehicle,
            is_active: editing.is_active,
            notes: editing.notes,
          }
        : { name: '', available_for_personal_use: true, has_another_vehicle: false, is_active: true },
    );
    setSeeded(seedKey);
  }

  function upd<K extends keyof VehicleInput>(key: K, value: VehicleInput[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!form.name?.trim()) {
      toast.error('Give the vehicle a name');
      return;
    }
    await save.mutateAsync({ id: editing?.id, input: form });
    toast.success(editing ? 'Vehicle updated' : 'Vehicle added');
    setSeeded(null);
    onClose();
  }

  return (
    <Dialog open={state.open} onClose={onClose} title={editing ? 'Edit vehicle' : 'Add vehicle'} size="md">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Name">
          <Input
            placeholder="2020 Honda CR-V"
            value={form.name ?? ''}
            onChange={(e) => upd('name', e.target.value)}
            required
          />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Make">
            <Input value={form.make ?? ''} onChange={(e) => upd('make', e.target.value)} />
          </Field>
          <Field label="Model">
            <Input value={form.model ?? ''} onChange={(e) => upd('model', e.target.value)} />
          </Field>
          <Field label="Year">
            <Input
              type="number"
              value={form.year ?? ''}
              onChange={(e) => upd('year', e.target.value ? Number(e.target.value) : null)}
            />
          </Field>
        </div>
        <Field label="Date placed in service">
          <Input
            type="date"
            value={form.date_placed_in_service ?? ''}
            onChange={(e) => upd('date_placed_in_service', e.target.value || null)}
          />
        </Field>
        <div className="space-y-2 pt-1">
          <Check
            label="Available for personal use"
            checked={form.available_for_personal_use ?? true}
            onChange={(v) => upd('available_for_personal_use', v)}
          />
          <Check
            label="I have another vehicle for personal use"
            checked={form.has_another_vehicle ?? false}
            onChange={(v) => upd('has_another_vehicle', v)}
          />
          <Check label="Active" checked={form.is_active ?? true} onChange={(v) => upd('is_active', v)} />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function TripDialog({
  state,
  vehicles,
  year,
  onClose,
}: {
  state: { open: boolean; editing?: MileageTripData };
  vehicles: VehicleData[];
  year: number;
  onClose: () => void;
}) {
  const save = useSaveTrip();
  const toast = useToast();
  const clients = useClients({ status: 'active' });
  const editing = state.editing;
  const today = new Date().toISOString().slice(0, 10);

  const [seeded, setSeeded] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<TripInput>({
    vehicle_id: vehicles[0]?.id ?? 0,
    trip_date: today,
    purpose: '',
    business_miles: 0,
  });

  const seedKey = editing ? editing.id : state.open ? 'new' : null;
  if (seedKey !== null && seeded !== seedKey) {
    setForm(
      editing
        ? {
            vehicle_id: editing.vehicle_id,
            trip_date: editing.trip_date,
            purpose: editing.purpose,
            business_miles: Number(editing.business_miles),
            client_id: editing.client_id,
            notes: editing.notes,
          }
        : {
            vehicle_id: vehicles[0]?.id ?? 0,
            trip_date: `${year}-${today.slice(5)}`,
            purpose: '',
            business_miles: 0,
          },
    );
    setSeeded(seedKey);
  }

  function upd<K extends keyof TripInput>(key: K, value: TripInput[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!form.vehicle_id) {
      toast.error('Pick a vehicle');
      return;
    }
    if (!form.purpose.trim()) {
      toast.error('Add a trip purpose');
      return;
    }
    if (!(form.business_miles > 0)) {
      toast.error('Enter the business miles');
      return;
    }
    await save.mutateAsync({ id: editing?.id, input: form });
    toast.success(editing ? 'Trip updated' : 'Trip logged');
    setSeeded(null);
    onClose();
  }

  return (
    <Dialog open={state.open} onClose={onClose} title={editing ? 'Edit trip' : 'Log trip'} size="md">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vehicle">
            <Select
              value={form.vehicle_id}
              onChange={(e) => upd('vehicle_id', Number(e.target.value))}
            >
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date">
            <Input
              type="date"
              value={form.trip_date}
              onChange={(e) => upd('trip_date', e.target.value)}
              required
            />
          </Field>
        </div>
        <Field label="Purpose / destination">
          <Input
            placeholder="Client site visit — Acme"
            value={form.purpose}
            onChange={(e) => upd('purpose', e.target.value)}
            required
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Business miles">
            <Input
              type="number"
              step="0.1"
              min="0"
              value={form.business_miles || ''}
              onChange={(e) => upd('business_miles', parseFloat(e.target.value) || 0)}
              required
            />
          </Field>
          <Field label="Client (optional)">
            <Select
              value={form.client_id ?? ''}
              onChange={(e) => upd('client_id', e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">—</option>
              {(clients.data?.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Notes">
          <Textarea
            rows={2}
            value={form.notes ?? ''}
            onChange={(e) => upd('notes', e.target.value || null)}
          />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="font-medium text-[var(--color-foreground-muted)]">{label}</span>
      {children}
    </label>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4"
      />
      <span>{label}</span>
    </label>
  );
}
